import React, { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Loader2, Plus, RefreshCw, Send, Trash2 } from 'lucide-react';
import { BOTON_VERDE, FOCO, Interruptor, comoObjeto, cuando } from './comun';

/**
 * Las plantillas del bot (las que salen fuera de la ventana de 24 h): la
 * apertura a cada lead nuevo (con y sin nombre, con tres botones), el
 * seguimiento a las 24 h y el cierre a las 72 h (también con y sin nombre) y
 * el aviso de traspaso al WhatsApp del equipo. Qué va en cada variable lo
 * dice el servidor (`variables`). Sus textos viven en el servidor y se editan aquí;
 * «Mandar a aprobación» las crea en el proveedor conectado para que Meta las
 * apruebe. Además: el interruptor de los seguimientos automáticos, las
 * marcas a cuyos leads se escribe la apertura, hasta cuántas horas después de
 * entrar se les escribe y las tablas que rellenan sus variables (tema y
 * procedencia).
 * Nada de esto viene escrito en el código.
 */

const SIN_NOMBRE = 'La misma, para cuando el lead no dejó nombre: Meta no admite una variable vacía.';
const INFO = {
  apertura: ['Apertura', 'El primer mensaje a cada lead nuevo que no entra descartado, con tres botones de respuesta rápida.'],
  apertura_sin_nombre: ['Apertura sin nombre', SIN_NOMBRE],
  seguimiento_24h: ['Seguimiento a las 24 h', 'Si el lead no ha contestado a la apertura.'],
  seguimiento_24h_sin_nombre: ['Seguimiento a las 24 h, sin nombre', SIN_NOMBRE],
  cierre_72h: ['Cierre a las 72 h', 'Si sigue sin contestar. Después ya no se le escribe más.'],
  cierre_72h_sin_nombre: ['Cierre a las 72 h, sin nombre', SIN_NOMBRE],
  aviso_traspaso: ['Aviso de traspaso', 'La que recibe el WhatsApp de avisos cuando el bot pasa un lead al equipo y ese número no ha escrito en 24 h.'],
};

const ESTADOS = {
  sin_mandar: ['Sin mandar', 'bg-zinc-100 text-zinc-700 ring-zinc-300'],
  PENDING: ['En revisión de Meta', 'bg-amber-100 text-amber-900 ring-amber-300'],
  APPROVED: ['Aprobada', 'bg-emerald-100 text-emerald-800 ring-emerald-300'],
  REJECTED: ['Rechazada', 'bg-red-100 text-red-800 ring-red-300'],
  PAUSED: ['En pausa', 'bg-amber-100 text-amber-900 ring-amber-300'],
  DISABLED: ['Desactivada', 'bg-red-100 text-red-800 ring-red-300'],
};

const CATEGORIAS = [['MARKETING', 'Marketing'], ['UTILITY', 'Utilidad']];

export const PROCEDENCIAS = [
  ['meta_instagram', 'Anuncio de Meta en Instagram'],
  ['meta_facebook', 'Anuncio de Meta en Facebook'],
  ['meta', 'Meta sin plataforma'],
  ['web', 'Web'],
  ['doctoralia', 'Doctoralia'],
  ['instagram', 'Instagram directo'],
  ['llamada', 'Llamada'],
  ['otras', 'Otras'],
];

const RE_NOMBRE = /^[a-z0-9_]{1,512}$/;

/** Las variables {{n}} que lleva un texto, en orden: ['1', '2', '3']. */
export function variablesDe(texto) {
  const vistas = new Set();
  String(texto || '').replace(/\{\{\s*(\d{1,2})\s*\}\}/g, (todo, n) => { vistas.add(String(Number(n))); return todo; });
  return [...vistas].sort((a, b) => Number(a) - Number(b));
}

function formDe(p) {
  return {
    nombre: p.nombre || '',
    idioma: p.idioma || '',
    categoria: p.categoria || 'MARKETING',
    texto: p.texto || '',
    botones: Array.isArray(p.botones) ? p.botones.map((b) => b || '') : [],
    ejemplos: { ...comoObjeto(p.ejemplos) },
  };
}

export function ChipEstadoProveedor({ estado }) {
  const [texto, clase] = ESTADOS[estado] || [estado || 'Sin mandar', ESTADOS.sin_mandar[1]];
  return (
    <span data-testid="bot-plantilla-estado" data-valor={estado || 'sin_mandar'}
      className={`inline-flex items-center rounded-full px-2 py-px text-[11px] font-semibold ring-1 ring-inset ${clase}`}>
      {texto}
    </span>
  );
}

function TarjetaPlantilla({ p, conectado, onCambio }) {
  const inicial = formDe(p);
  const [form, setForm] = useState(inicial);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [mandando, setMandando] = useState(false);
  const [titulo, explica] = INFO[p.clave] || [p.clave, ''];
  const cambiado = JSON.stringify(form) !== JSON.stringify(inicial);
  // Qué va en cada variable lo dice el servidor; uno anterior no lo manda y
  // entonces valen las que lleve el texto.
  const conApi = Array.isArray(p.variables);
  const varsApi = conApi ? p.variables.map((v) => String(v.n)) : [];
  const varsTexto = variablesDe(form.texto);
  const vars = conApi ? varsApi.filter((v) => varsTexto.includes(v)) : varsTexto;
  const sobran = conApi ? varsTexto.filter((v) => !varsApi.includes(v)) : [];
  const queEs = conApi ? Object.fromEntries(p.variables.map((v) => [String(v.n), v.es])) : {};
  const conBotones = form.botones.length > 0;
  const id = `bot-plantilla-${p.clave}`;

  const poner = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const cuerpo = () => ({
    nombre: form.nombre.trim(),
    idioma: form.idioma.trim(),
    categoria: form.categoria,
    texto: form.texto.trim(),
    botones: form.botones.map((b) => b.trim()),
    ejemplos: Object.fromEntries(vars.map((v) => [v, (form.ejemplos[v] || '').trim()])),
  });

  const guardar = async (e) => {
    e?.preventDefault();
    if (guardando) return;
    const c = cuerpo();
    if (c.nombre && !RE_NOMBRE.test(c.nombre)) {
      setError('El nombre solo puede llevar minúsculas sin acentos, cifras y guiones bajos (lo exige Meta).');
      return;
    }
    setError('');
    setGuardando(true);
    try {
      const r = await api.put(`/api/whatsapp/bot/plantillas/${p.clave}`, c);
      onCambio(r.data);
      toast.success(`Plantilla «${titulo}» guardada`);
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido guardar la plantilla'));
    } finally {
      setGuardando(false);
    }
  };

  const faltaEjemplo = vars.some((v) => !(form.ejemplos[v] || '').trim());
  const faltaBoton = conBotones && form.botones.some((b) => !b.trim());
  let motivoNoMandar = '';
  if (!conectado) motivoNoMandar = 'Hace falta WhatsApp conectado (pestaña Conexión).';
  else if (cambiado) motivoNoMandar = 'Guarda los cambios antes de mandarla.';
  else if (!form.nombre.trim() || !form.texto.trim()) motivoNoMandar = 'Faltan el nombre o el texto.';
  else if (sobran.length) motivoNoMandar = `Esta plantilla no lleva ${sobran.map((v) => `{{${v}}}`).join(', ')}.`;
  else if (faltaBoton) motivoNoMandar = 'Faltan los textos de los botones.';
  else if (faltaEjemplo) motivoNoMandar = 'Falta un ejemplo para cada variable: Meta los pide para revisarla.';

  const mandar = async () => {
    if (motivoNoMandar || mandando) return;
    if (!window.confirm(`¿Mandar «${form.nombre.trim()}» a aprobación de Meta? Se crea en el proveedor de WhatsApp `
      + 'con el texto guardado y Meta contesta en unas horas (a veces hasta 48 h). Mientras no esté aprobada no se usa.')) return;
    setMandando(true);
    try {
      const r = await api.post(`/api/whatsapp/bot/plantillas/${p.clave}/aprobacion`);
      onCambio(r.data);
      toast.success('Plantilla mandada a aprobación');
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido mandar la plantilla a aprobación'));
    } finally {
      setMandando(false);
    }
  };

  return (
    <form onSubmit={guardar} className="rounded-lg border p-3 space-y-2.5" data-testid={id} noValidate>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[#13231E]">{titulo}</h3>
          {explica ? <p className="text-xs text-muted-foreground max-w-2xl">{explica}</p> : null}
        </div>
        <div className="text-right space-y-0.5">
          <ChipEstadoProveedor estado={p.estado_proveedor} />
          {p.mandada_at ? <div className="text-[11px] text-muted-foreground">Mandada el {cuando(p.mandada_at)}</div> : null}
        </div>
      </div>
      {p.motivo ? (
        <p className={`text-xs rounded px-2 py-1 border ${p.estado_proveedor === 'REJECTED'
          ? 'text-red-800 bg-red-50 border-red-200' : 'text-amber-900 bg-amber-50 border-amber-200'}`}
          data-testid={`${id}-motivo`}>
          {p.estado_proveedor === 'REJECTED' ? `Motivo de Meta: ${p.motivo}` : p.motivo}
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-[1fr_6rem_9rem]">
        <div className="space-y-0.5 min-w-0">
          <label htmlFor={`${id}-nombre`} className="text-xs text-muted-foreground">Nombre en Meta (minúsculas y _)</label>
          <Input id={`${id}-nombre`} value={form.nombre} onChange={(e) => poner('nombre', e.target.value)}
            autoComplete="off" spellCheck={false} className="h-8 font-mono text-xs" data-testid={`${id}-nombre`} />
        </div>
        <div className="space-y-0.5">
          <label htmlFor={`${id}-idioma`} className="text-xs text-muted-foreground">Idioma</label>
          <Input id={`${id}-idioma`} value={form.idioma} onChange={(e) => poner('idioma', e.target.value)}
            autoComplete="off" className="h-8" data-testid={`${id}-idioma`} />
        </div>
        <div className="space-y-0.5">
          <label htmlFor={`${id}-categoria`} className="text-xs text-muted-foreground">Categoría</label>
          <select id={`${id}-categoria`} value={form.categoria} onChange={(e) => poner('categoria', e.target.value)}
            className={`flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm ${FOCO}`}
            data-testid={`${id}-categoria`}>
            {CATEGORIAS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
            {!CATEGORIAS.some(([v]) => v === form.categoria) ? <option value={form.categoria}>{form.categoria}</option> : null}
          </select>
        </div>
      </div>
      <div className="space-y-0.5">
        <label htmlFor={`${id}-texto`} className="text-xs text-muted-foreground">Texto (las variables, como {'{{1}}'}, {'{{2}}'}…)</label>
        <Textarea id={`${id}-texto`} value={form.texto} onChange={(e) => poner('texto', e.target.value)} rows={4}
          data-testid={`${id}-texto`} aria-describedby={conApi ? `${id}-variables` : undefined} />
        {conApi ? (
          <p id={`${id}-variables`} className="text-xs text-muted-foreground" data-testid={`${id}-variables`}>
            {varsApi.length ? p.variables.map((v) => `{{${v.n}}} = ${v.es}`).join(' · ') : 'Sin variables: el texto sale tal cual.'}
          </p>
        ) : null}
        {sobran.length ? (
          <p className="text-xs text-amber-800" data-testid={`${id}-sobran`}>
            Esta plantilla no lleva {sobran.map((v) => `{{${v}}}`).join(', ')}: el servidor no sabría qué poner ahí.
          </p>
        ) : null}
      </div>
      {conBotones ? (
        <div className="space-y-0.5">
          <div className="text-xs text-muted-foreground" id={`${id}-botones-titulo`}>Botones de respuesta rápida</div>
          <div className="grid gap-2 sm:grid-cols-3" role="group" aria-labelledby={`${id}-botones-titulo`}>
            {form.botones.map((b, i) => (
              // Los botones son posiciones fijas: el índice es su identidad.
              <Input key={i} value={b} maxLength={25} aria-label={`Botón ${i + 1}`}
                onChange={(e) => setForm((f) => ({ ...f, botones: f.botones.map((x, j) => (j === i ? e.target.value : x)) }))}
                className="h-8" data-testid={`${id}-boton-${i + 1}`} />
            ))}
          </div>
        </div>
      ) : null}
      {vars.length ? (
        <div className="space-y-0.5">
          <div className="text-xs text-muted-foreground">Ejemplos para Meta (un valor de muestra por variable)</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {vars.map((v) => (
              <label key={v} className="flex items-center gap-2 text-sm" title={queEs[v] || undefined}>
                <span className="w-10 shrink-0 font-mono text-xs text-muted-foreground">{`{{${v}}}`}</span>
                <Input value={form.ejemplos[v] || ''} aria-label={`Ejemplo de la variable ${v}`}
                  onChange={(e) => setForm((f) => ({ ...f, ejemplos: { ...f.ejemplos, [v]: e.target.value } }))}
                  className="h-8" data-testid={`${id}-ejemplo-${v}`} />
              </label>
            ))}
          </div>
        </div>
      ) : null}
      {error ? <p className="text-xs text-red-700" data-testid={`${id}-error`}>{error}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={!cambiado || guardando} className={BOTON_VERDE}
          data-testid={`${id}-guardar`}>
          {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : null} Guardar
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={mandar} disabled={!!motivoNoMandar || mandando}
          aria-describedby={motivoNoMandar ? `${id}-por-que` : undefined} data-testid={`${id}-aprobacion`}>
          {mandando ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <Send className="w-3.5 h-3.5" aria-hidden="true" />}
          {p.estado_proveedor && p.estado_proveedor !== 'sin_mandar' ? 'Volver a mandar a aprobación' : 'Mandar a aprobación'}
        </Button>
        {motivoNoMandar ? <span id={`${id}-por-que`} className="text-xs text-muted-foreground" data-testid={`${id}-por-que`}>{motivoNoMandar}</span> : null}
      </div>
    </form>
  );
}

const HORAS_APERTURA_POR_DEFECTO = 48;

/** «Solo se escribe la apertura a leads que han entrado en las últimas N horas». */
function HorasApertura({ valor, onCambio }) {
  const inicial = String(valor ?? HORAS_APERTURA_POR_DEFECTO);
  const [texto, setTexto] = useState(inicial);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const guardar = async (e) => {
    e?.preventDefault();
    if (guardando) return;
    const t = texto.trim();
    if (!/^\d+$/.test(t) || Number(t) < 1) { setError('Un número entero de horas, de 1 en adelante.'); return; }
    setError('');
    setGuardando(true);
    try {
      const r = await api.put('/api/whatsapp/bot/plantillas-ajustes', { apertura_max_horas: Number(t) });
      onCambio(r.data);
      toast.success('Guardado: horas para la apertura');
    } catch (err) {
      toast.error(mensajeError(err, 'No se han podido guardar las horas de la apertura'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={guardar} className="space-y-1" data-testid="bot-apertura-horas" noValidate>
      <label htmlFor="bot-apertura-max-horas" className="text-sm font-medium text-[#13231E]">
        Hasta cuándo se escribe la apertura
      </label>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>Solo se escribe la apertura a leads que han entrado en las últimas</span>
        <Input id="bot-apertura-max-horas" type="number" inputMode="numeric" min={1} step={1} value={texto}
          onChange={(e) => setTexto(e.target.value)} className="h-8 w-20" data-testid="bot-apertura-max-horas" />
        <span>horas.</span>
        <Button type="submit" size="sm" disabled={guardando || texto.trim() === inicial} className={BOTON_VERDE}
          data-testid="bot-apertura-max-horas-guardar">
          {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : null} Guardar
        </Button>
      </div>
      {error ? <p className="text-xs text-red-700" data-testid="bot-apertura-max-horas-error">{error}</p> : null}
      <p className="text-xs text-muted-foreground">A los que entraron antes no se les escribe primero.</p>
    </form>
  );
}

let filaId = 0;
const nuevaFila = (tratamiento = '', texto = '') => { filaId += 1; return { id: `f${filaId}`, tratamiento, texto }; };

function AjustesPlantillas({ datos, onCambio }) {
  const procedenciasServidor = comoObjeto(datos.procedencias);
  const claves = [...PROCEDENCIAS.map(([k]) => k),
    ...Object.keys(procedenciasServidor).filter((k) => !PROCEDENCIAS.some(([c]) => c === k))];
  const nombres = Object.fromEntries(PROCEDENCIAS);
  const [filas, setFilas] = useState(() => Object.entries(comoObjeto(datos.temas)).map(([t, x]) => nuevaFila(t, x || '')));
  const [porDefecto, setPorDefecto] = useState(datos.tema_por_defecto || '');
  const [procedencias, setProcedencias] = useState(() => Object.fromEntries(claves.map((k) => [k, procedenciasServidor[k] || ''])));
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cambiandoSeg, setCambiandoSeg] = useState(false);
  const [cambiandoMarcas, setCambiandoMarcas] = useState(false);
  const marcas = Array.isArray(datos.marcas) ? datos.marcas : [];
  // Las externas (p. ej. un portal) no se ofrecen: el bot escribe a los leads
  // de las marcas propias. Si alguna ya estaba marcada, sale para poder quitarla.
  const marcasPosibles = (Array.isArray(datos.marcas_posibles) ? datos.marcas_posibles : [])
    .filter((m) => m && m.clave && (!m.externa || marcas.includes(m.clave)));
  const [tocado, setTocado] = useState(false);

  const base = () => ({
    seguimientos_activos: !!datos.seguimientos_activos,
    temas: comoObjeto(datos.temas),
    tema_por_defecto: datos.tema_por_defecto || '',
    procedencias: procedenciasServidor,
  });

  const cambiarSeguimientos = async (valor) => {
    if (cambiandoSeg) return;
    if (valor && !window.confirm('Con esto, a quien no conteste a la apertura se le manda el seguimiento a las 24 h y, '
      + 'si sigue sin contestar, el cierre a las 72 h. Solo con el bot «Enviando» y las plantillas aprobadas. ¿Encender?')) return;
    setCambiandoSeg(true);
    try {
      const r = await api.put('/api/whatsapp/bot/plantillas-ajustes', { ...base(), seguimientos_activos: valor });
      onCambio(r.data);
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido cambiar el ajuste'));
    } finally {
      setCambiandoSeg(false);
    }
  };

  // Cada casilla se guarda al momento, como el interruptor: manda la lista entera.
  const alternarMarca = async (clave) => {
    if (cambiandoMarcas) return;
    const nuevas = marcas.includes(clave) ? marcas.filter((m) => m !== clave) : [...marcas, clave];
    setCambiandoMarcas(true);
    try {
      const r = await api.put('/api/whatsapp/bot/plantillas-ajustes', { marcas: nuevas });
      onCambio(r.data);
    } catch (err) {
      toast.error(mensajeError(err, 'No se han podido cambiar las marcas'));
    } finally {
      setCambiandoMarcas(false);
    }
  };

  const tocar = (fn) => { setTocado(true); fn(); };

  const guardar = async (e) => {
    e?.preventDefault();
    if (guardando) return;
    const temas = {};
    for (const f of filas) {
      const t = f.tratamiento.trim();
      if (!t && !f.texto.trim()) continue; // fila vacía: se ignora
      if (!t) { setError('Hay una fila con texto pero sin tratamiento.'); return; }
      const repetido = Object.keys(temas).find((k) => k.toLowerCase() === t.toLowerCase());
      if (repetido) { setError(`El tratamiento «${t}» está dos veces.`); return; }
      temas[t] = f.texto.trim();
    }
    setError('');
    setGuardando(true);
    try {
      const r = await api.put('/api/whatsapp/bot/plantillas-ajustes', {
        seguimientos_activos: !!datos.seguimientos_activos,
        temas,
        tema_por_defecto: porDefecto.trim(),
        procedencias: Object.fromEntries(Object.entries(procedencias).map(([k, v]) => [k, v.trim()])),
      });
      setTocado(false);
      onCambio(r.data);
      toast.success('Textos de la apertura guardados');
    } catch (err) {
      toast.error(mensajeError(err, 'No se han podido guardar los textos de la apertura'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bot-plantillas-ajustes">
      <Interruptor id="bot-seguimientos" activo={!!datos.seguimientos_activos} onCambio={cambiarSeguimientos}
        disabled={cambiandoSeg} titulo="Seguimientos automáticos" testid="bot-seguimientos">
        Seguimiento a las 24 h y cierre a las 72 h a quien no contesta, sin pasar de 3 mensajes seguidos sin respuesta.
        Apagado, después de la apertura no se le vuelve a escribir.
      </Interruptor>

      <div className="space-y-1.5" data-testid="bot-marcas">
        <div className="text-sm font-medium text-[#13231E]" id="bot-marcas-titulo">Marcas a las que el bot escribe</div>
        <p className="text-xs text-muted-foreground">
          La apertura solo sale a los leads nuevos de las marcas marcadas. Se guarda al marcar o desmarcar.
        </p>
        {marcasPosibles.length ? (
          <div className="grid gap-1 sm:grid-cols-2" role="group" aria-labelledby="bot-marcas-titulo">
            {marcasPosibles.map((m) => (
              <label key={m.clave} className="flex items-center gap-2 text-sm rounded px-1 py-0.5 hover:bg-muted/40">
                <input type="checkbox" checked={marcas.includes(m.clave)} disabled={cambiandoMarcas}
                  onChange={() => alternarMarca(m.clave)} data-testid={`bot-marca-${m.clave}`} />
                <span>{m.nombre || m.clave}</span>
                {m.externa ? <span className="text-xs text-muted-foreground">· externa</span> : null}
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="bot-marcas-ninguna">
            No hay ninguna marca dada de alta en Configuración.
          </p>
        )}
        {!marcas.length ? (
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5"
            data-testid="bot-marcas-vacio" role="status">
            Sin marcas marcadas, el bot no escribe la apertura a nadie.
          </p>
        ) : null}
      </div>

      <HorasApertura key={String(datos.apertura_max_horas)} valor={datos.apertura_max_horas} onCambio={onCambio} />

      <form onSubmit={guardar} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <div className="text-sm font-medium text-[#13231E]">Temas: la variable {'{{2}}'} de la apertura</div>
          <p className="text-xs text-muted-foreground">
            Según el tratamiento que marcó en el formulario (no importan mayúsculas ni acentos). Si no está en la tabla,
            o no marcó ninguno, va el texto por defecto.
          </p>
          <div className="space-y-1.5" data-testid="bot-temas">
            {filas.map((f, i) => (
              <div key={f.id} className="grid gap-1.5 sm:grid-cols-[1fr_1fr_auto] items-center" data-testid="bot-tema">
                <Input value={f.tratamiento} aria-label={`Tratamiento del formulario, fila ${i + 1}`}
                  placeholder="Tratamiento del formulario" className="h-8"
                  onChange={(e) => tocar(() => setFilas((fs) => fs.map((x) => (x.id === f.id ? { ...x, tratamiento: e.target.value } : x))))}
                  data-testid={`bot-tema-tratamiento-${i}`} />
                <Input value={f.texto} aria-label={`Texto de {{2}}, fila ${i + 1}`} placeholder="Texto de {{2}}"
                  className="h-8"
                  onChange={(e) => tocar(() => setFilas((fs) => fs.map((x) => (x.id === f.id ? { ...x, texto: e.target.value } : x))))}
                  data-testid={`bot-tema-texto-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 justify-self-end"
                  aria-label={`Quitar la fila ${i + 1}${f.tratamiento ? ` (${f.tratamiento})` : ''}`}
                  onClick={() => tocar(() => setFilas((fs) => fs.filter((x) => x.id !== f.id)))}
                  data-testid={`bot-tema-quitar-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {!filas.length ? <p className="text-xs text-muted-foreground">Sin temas todavía.</p> : null}
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => tocar(() => setFilas((fs) => [...fs, nuevaFila()]))}
            data-testid="bot-tema-anadir">
            <Plus className="w-3.5 h-3.5" aria-hidden="true" /> Añadir fila
          </Button>
          <div className="space-y-0.5 max-w-md pt-1">
            <label htmlFor="bot-tema-defecto" className="text-xs text-muted-foreground">Texto por defecto</label>
            <Input id="bot-tema-defecto" value={porDefecto} className="h-8"
              onChange={(e) => tocar(() => setPorDefecto(e.target.value))} data-testid="bot-tema-defecto" />
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="text-sm font-medium text-[#13231E]">Procedencias: la variable {'{{3}}'} de la apertura</div>
          <p className="text-xs text-muted-foreground">El texto según por dónde llegó el lead.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {claves.map((k) => (
              <div key={k} className="space-y-0.5">
                <label htmlFor={`bot-procedencia-${k}`} className="text-xs text-muted-foreground">{nombres[k] || k}</label>
                <Input id={`bot-procedencia-${k}`} value={procedencias[k] || ''} className="h-8"
                  onChange={(e) => tocar(() => setProcedencias((p) => ({ ...p, [k]: e.target.value })))}
                  data-testid={`bot-procedencia-${k}`} />
              </div>
            ))}
          </div>
        </div>
        {error ? <p className="text-xs text-red-700" data-testid="bot-plantillas-ajustes-error">{error}</p> : null}
        <Button type="submit" size="sm" disabled={!tocado || guardando} className={BOTON_VERDE}
          data-testid="bot-plantillas-ajustes-guardar">
          {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : null}
          Guardar temas y procedencias
        </Button>
      </form>
    </div>
  );
}

export default function PlantillasBot({ datos, fallo, onCambio, onRecargar }) {
  if (fallo) {
    return (
      <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3 flex flex-wrap items-center gap-2"
        data-testid="bot-plantillas-fallo">
        <span className="flex-1 min-w-0">{fallo}</span>
        <Button type="button" size="sm" variant="outline" onClick={onRecargar}>
          <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Reintentar
        </Button>
      </div>
    );
  }
  if (!datos) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Cargando las plantillas…
      </div>
    );
  }
  const d = comoObjeto(datos);
  const lista = Array.isArray(d.plantillas) ? d.plantillas : [];
  const firmaAjustes = JSON.stringify([d.temas, d.tema_por_defecto, d.procedencias]);
  return (
    <div className="space-y-4">
      {d.apertura_motivo ? (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5" role="status"
          data-testid="bot-apertura-motivo">
          {d.apertura_motivo}
        </p>
      ) : null}
      {!d.conectado ? (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5" data-testid="bot-plantillas-sin-conexion">
          WhatsApp no está conectado: se pueden preparar y guardar, pero para mandarlas a aprobación hay que conectarlo antes.
        </p>
      ) : null}
      <div className="grid gap-3 xl:grid-cols-2">
        {lista.map((p) => (
          <TarjetaPlantilla key={`${p.clave}-${JSON.stringify(p)}`} p={p} conectado={!!d.conectado} onCambio={onCambio} />
        ))}
        {!lista.length ? <p className="text-sm text-muted-foreground">El servidor no ha devuelto ninguna plantilla.</p> : null}
      </div>
      <div className="border-t pt-4">
        <AjustesPlantillas key={firmaAjustes} datos={d} onCambio={onCambio} />
      </div>
    </div>
  );
}
